import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, Home, LayoutDashboard, MessageCircle, Phone } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_presencia-digital-2/artifacts/19ezorhg_IMG_0946.jpeg";

export const Logo = ({ size = 36, withWordmark = false }) => (
  <div className="flex items-center gap-2" data-testid="brand-logo">
    <img
      src={LOGO_URL}
      alt="En Línea Madrid"
      style={{ height: size, width: size }}
      className="rounded-lg object-contain bg-white"
    />
    {withWordmark && (
      <div className="leading-none">
        <div className="font-[Outfit] font-bold text-slate-900 text-sm">
          En<span className="text-[var(--el-red)]">Línea</span>
        </div>
        <div className="text-[10px] tracking-[0.25em] text-slate-500 font-medium">
          MADRID
        </div>
      </div>
    )}
  </div>
);

export const AppHeader = ({ showBack = false, title, right }) => {
  const navigate = useNavigate();
  return (
    <header
      className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-100"
      data-testid="app-header"
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          {showBack ? (
            <button
              onClick={() => navigate(-1)}
              className="h-9 w-9 -ml-1 flex items-center justify-center rounded-full hover:bg-slate-100 active:scale-95 transition"
              data-testid="btn-back"
              aria-label="Volver"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
          ) : null}
          <Logo size={32} withWordmark />
        </div>
        <div className="flex items-center gap-2">{right}</div>
      </div>
      {title && (
        <div className="px-5 pt-1 pb-3">
          <h1 className="text-2xl sm:text-3xl font-[Outfit] font-bold tracking-tight text-slate-900">
            {title}
          </h1>
        </div>
      )}
    </header>
  );
};

const TABS = [
  { to: "/", label: "Inicio", icon: Home, testid: "nav-home" },
  { to: "/panel", label: "Panel", icon: LayoutDashboard, testid: "nav-panel" },
  { to: "/soporte", label: "Soporte", icon: MessageCircle, testid: "nav-support" },
  { to: "/contacto", label: "Contacto", icon: Phone, testid: "nav-contact" },
];

export const BottomNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <nav
      className="sticky bottom-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-100"
      data-testid="bottom-nav"
    >
      <div className="grid grid-cols-4">
        {TABS.map(({ to, label, icon: Icon, testid }) => {
          const active = pathname === to || (to === "/panel" && pathname.startsWith("/panel"));
          return (
            <button
              key={to}
              onClick={() => navigate(to)}
              data-testid={testid}
              className="flex flex-col items-center gap-1 py-2.5 active:scale-95 transition"
            >
              <Icon
                className={`w-[22px] h-[22px] ${
                  active ? "text-[var(--el-red)]" : "text-slate-500"
                }`}
              />
              <span
                className={`text-[11px] font-medium ${
                  active ? "text-[var(--el-red)]" : "text-slate-500"
                }`}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export const Footer = () => (
  <footer className="px-5 py-6 border-t border-slate-100 mt-auto" data-testid="app-footer">
    <div className="flex items-center gap-3">
      <Logo size={28} />
      <div>
        <div className="font-[Outfit] font-semibold text-slate-900 text-sm leading-tight">
          En Línea Madrid
        </div>
        <p className="text-xs text-slate-500">Soluciones digitales para negocios</p>
      </div>
    </div>
  </footer>
);

export default function AppShell({ children, showBack = false, title, withBottomNav = true, right }) {
  return (
    <div className="app-shell">
      <div className="app-frame flex flex-col">
        <AppHeader showBack={showBack} title={title} right={right} />
        <main className="flex-1 flex flex-col fade-in">{children}</main>
        <Footer />
        {withBottomNav && <BottomNav />}
      </div>
    </div>
  );
}
